"use client"

/**
 * AHD-6470 — membership-vs-one-off comparison for /go/bloodtest.
 *
 * Two cards side by side, ported from the booking-flow "Blood tracking"
 * layout (ahead-booking-new BloodTrackingLayout + MembershipFrequencyCard):
 *
 *   left  → Ahead membership (recommended, lavender-tinted, "Most popular",
 *           from CHF 299/year, full 6-row checklist all green)
 *   right → One-off blood test (white, "Essentials", CHF 299, same 6 rows
 *           but only the first 3 checked — the asymmetry IS the comparison)
 *
 * Each card has its own CTA: membership → Ahead Membership sign-up,
 * one-off → booking flow with the blood product preselected.
 *
 * Copy lives in the `go` namespace. Strings whose English is byte-identical to
 * an existing `go.bloodtestCompare*` / `go.membershipHero*` entry reuse that
 * key rather than duplicating a translation across all seven locale files —
 * those reuses are marked inline below.
 *
 * Currency: the amounts below are hardcoded in Swiss francs (unchanged from
 * the prototype, so they carry the pre-existing price-lint baseline). The only
 * page rendering this component (/tests/lpa) is CH-priced and 404s for the NL
 * market, so no EUR variant exists yet — market-adapted amounts would need
 * `lib/pricing` wiring, as in blood-tracking-comparison.tsx.
 */

import Image from "next/image"
import { Button, ArrowRightIcon } from "@/components/ui/button"
import { BookingCta, PRODUCT_IDS } from "@/components/ui/booking-cta"
import { CheckCircleIcon, XCircleIcon } from "@/components/ui/icons"
import { MembershipPillIcon } from "@/components/ui/new-membership-pill"
import { blobMediaUrl } from "@/lib/blob-url"
import { cn } from "@/lib/utils"
import Link from "next/link"
import { useTranslations } from "@/lib/intl/use-translations"
import { useLocalePath } from "@/lib/use-localized-navigation"

type GoTranslator = ReturnType<typeof useTranslations<"go">>

/* The key unions stay narrow (rather than the whole `go` keyspace) so `t(key)`
   still knows none of these messages take ICU values — widening to every `go`
   key would make the compiler demand the union of every placeholder in the
   namespace at each call site. */
type BenefitRowKey =
  | "bloodMembershipRowBloodTest"
  | "bloodMembershipRowReport"
  | "bloodMembershipRowAddAnytime"
  | "bloodMembershipRowFourTests"
  | "bloodMembershipRowSupport"
  | "bloodMembershipRowMemberPricing"

type DetailedFeatureKey =
  | "bloodtestCompareBenefitAdvancedTest"
  | "bloodtestCompareBenefitReport"
  | "bloodtestCompareBenefitFourTests"
  | "bloodtestCompareBenefitPrevention"
  | "bloodtestCompareBenefitFollowup"
  | "bloodtestCompareBenefitUpload"
  | "bloodMembershipFeatureMriDiscount"
  | "bloodMembershipFeatureAddonDiscount"

type FeatureTagKey =
  | "bloodMembershipTagIncluded"
  | "bloodMembershipTagFlexible"
  | "bloodMembershipTagTenPercentOff"

/* 6 comparison rows. The first three are included in the one-off too
   (green check on both); the last three are membership-only (soft X on
   the one-off). Order + copy mirror MEMBERSHIP_BENEFIT_ROWS in the
   booking flow. */
const BENEFIT_ROW_KEYS: { key: BenefitRowKey; inOneOff: boolean }[] = [
  { key: "bloodMembershipRowBloodTest", inOneOff: true },
  { key: "bloodMembershipRowReport", inOneOff: true },
  { key: "bloodMembershipRowAddAnytime", inOneOff: true },
  { key: "bloodMembershipRowFourTests", inOneOff: false },
  { key: "bloodMembershipRowSupport", inOneOff: false },
  { key: "bloodMembershipRowMemberPricing", inOneOff: false },
]

/* Shared card shadow (from the booking one-off PackageCard). Applied to
   both cards so they read as one matched pair. */
const CARD_SHADOW = "shadow-[0px_3px_16px_0px_rgba(106,133,161,0.05)]"

/* `tagKey` is a translated chip; `tag` is a literal price chip (a number, not
   copy — nothing to translate). Six of the eight labels are reused from the
   booking-comparison benefit catalogue, whose English is identical. */
const DETAILED_MEMBERSHIP_FEATURES: {
  key: DetailedFeatureKey
  tagKey?: FeatureTagKey
  tag?: string
}[] = [
  {
    key: "bloodtestCompareBenefitAdvancedTest",
    tagKey: "bloodMembershipTagIncluded",
  },
  { key: "bloodtestCompareBenefitReport" },
  {
    key: "bloodtestCompareBenefitFourTests",
    tagKey: "bloodMembershipTagFlexible",
  },
  { key: "bloodMembershipFeatureMriDiscount", tag: "-CHF 259" },
  {
    key: "bloodMembershipFeatureAddonDiscount",
    tagKey: "bloodMembershipTagTenPercentOff",
  },
  { key: "bloodtestCompareBenefitPrevention" },
  { key: "bloodtestCompareBenefitFollowup" },
  { key: "bloodtestCompareBenefitUpload" },
]

function DetailedFeatureRow({ label, tag }: { label: string; tag?: string }) {
  return (
    <div className="flex items-center gap-3 py-2.5">
      <CheckCircleIcon size={20} className="flex-shrink-0" />
      <span className="type-body-md min-w-0 flex-1 text-neutral-900">
        {label}
      </span>
      {tag && (
        <span className="badge-subtle-hue-drift text-light-blue-900 type-body-sm inline-block shrink-0 rounded-full px-5 py-1 font-medium whitespace-nowrap normal-case">
          {tag}
        </span>
      )}
    </div>
  )
}

function MembershipCardDetailed({
  badgeLabel,
  t,
}: {
  badgeLabel: string
  t: GoTranslator
}) {
  return (
    <div
      className={cn(
        "pill-magic-stroke pill-magic-stroke-always relative flex flex-col rounded-[20px] bg-gradient-to-br from-[#FCFAFE] via-[#FAFBFE] to-[#F7FBFF] p-5 md:p-8",
        CARD_SHADOW
      )}
    >
      <div className="absolute top-0 right-12 z-10 -translate-y-[calc(50%+2px)]">
        <span className="badge-subtle-hue-drift text-light-blue-900 type-body-sm inline-block rounded-full px-5 py-1 font-medium normal-case">
          {badgeLabel}
        </span>
      </div>

      <div className="flex flex-col">
        <div className="flex items-center gap-3">
          <MembershipPillIcon size={40} />
          <h3 className="type-headline-md text-neutral-900">
            {/* Reused: EN identical to the booking-comparison card title. */}
            {t("bloodtestCompareMembershipTitle")}
          </h3>
        </div>
        <p className="type-body-md mt-2 text-neutral-500">
          {t("bloodMembershipDetailedTagline")}
        </p>
        <div className="bg-deep-green-50 mt-4 self-start rounded-[10px] px-4 py-2.5">
          <p className="type-body-md text-deep-green-700 font-medium">
            {t("bloodMembershipDetailedSavings", { price: "CHF 290" })}
          </p>
        </div>
      </div>

      <div className="mt-6 border-t border-neutral-200 pt-2">
        {DETAILED_MEMBERSHIP_FEATURES.map((row) => (
          <DetailedFeatureRow
            key={row.key}
            label={t(row.key)}
            tag={row.tagKey ? t(row.tagKey) : row.tag}
          />
        ))}
      </div>

      <div className="mt-6 border-t border-neutral-200 pt-6">
        <p className="text-neutral-900">
          <span className="type-display-sm">CHF 299</span>
          <span className="type-body-lg text-neutral-500">
            {" "}
            {t("bloodMembershipPerYear")}
          </span>
        </p>
        <p className="type-body-sm mt-1 text-neutral-500">
          {t("bloodMembershipBilling")}
        </p>
      </div>

      <div className="mt-6">
        <BookingCta
          forMembership
          trackingLabel="bloodtest_membership_card_cta"
          trackingLocation="go/bloodtest"
          className="block"
        >
          <Button
            variant="primary"
            className="group w-full"
            iconRight={
              <ArrowRightIcon className="transition-transform duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:translate-x-1" />
            }
          >
            {t("bloodMembershipCta")}
          </Button>
        </BookingCta>
      </div>
    </div>
  )
}

function BenefitRow({ label, included }: { label: string; included: boolean }) {
  return (
    <div className="flex items-center gap-2.5 py-1.5">
      {included ? (
        <CheckCircleIcon size={20} className="flex-shrink-0" />
      ) : (
        <XCircleIcon size={20} className="flex-shrink-0 text-neutral-300" />
      )}
      <span
        className={cn(
          "type-body-md",
          included ? "text-neutral-900" : "text-neutral-400"
        )}
      >
        {label}
      </span>
    </div>
  )
}

export function BloodMembershipComparison({
  membershipBadge,
  membershipVariant = "default",
}: {
  /** Overrides the translated "Most popular" badge. */
  membershipBadge?: string
  membershipVariant?: "default" | "detailed"
}) {
  const lp = useLocalePath()
  const t = useTranslations("go")
  const badgeLabel = membershipBadge ?? t("bloodMembershipBadge")
  return (
    <div className="mx-auto max-w-5xl px-4 md:px-10">
      {/* Both cards are row-span-4 grid-rows-subgrid so header / price / CTA /
          checklist sit on the same baseline across the two columns (same
          alignment trick as the /pricing PricingCard). */}
      <div className="grid grid-cols-1 items-start gap-6 md:grid-cols-2">
        {membershipVariant === "detailed" ? (
          <MembershipCardDetailed badgeLabel={badgeLabel} t={t} />
        ) : (
          /* ── Left: Ahead membership (recommended) ── */
          <div
            className={cn(
              "pill-magic-stroke pill-magic-stroke-always relative row-span-4 grid grid-rows-subgrid gap-y-6 rounded-[20px] bg-gradient-to-br from-[#FCFAFE] via-[#FAFBFE] to-[#F7FBFF] p-5 md:p-8",
              CARD_SHADOW
            )}
          >
            {/* "Most popular" badge straddling the top border — same treatment
                as the /pricing Advanced card, so it never collides with the
                product name. */}
            <div className="absolute top-0 right-12 z-10 -translate-y-[calc(50%+2px)]">
              <span className="badge-subtle-hue-drift text-light-blue-900 type-body-sm inline-block rounded-full px-5 py-1 font-medium normal-case">
                {badgeLabel}
              </span>
            </div>

            {/* Row 1: header */}
            <div className="flex flex-col">
              <div className="flex items-center gap-3">
                <MembershipPillIcon size={40} />
                <h3 className="type-headline-md text-neutral-900">
                  {/* Reused: EN identical to the booking-comparison title. */}
                  {t("bloodtestCompareMembershipTitle")}
                </h3>
              </div>
              <p className="type-body-md mt-2 text-neutral-500">
                {t("bloodMembershipTagline")}
              </p>
              <p className="type-body-lg mt-2 text-neutral-900">
                {t("bloodMembershipDescription")}
              </p>
            </div>

            {/* Row 2: price */}
            <div className="flex items-baseline gap-1">
              <span className="type-body-md text-neutral-500">
                {/* Reused: the shared "from" price prefix. */}
                {t("membershipHeroFromLabel")} CHF
              </span>
              <span className="type-display-sm text-neutral-900">299</span>
              <span className="type-body-md text-neutral-500">
                {t("bloodMembershipPerYearCancel")}
              </span>
            </div>

            {/* Row 3: CTA */}
            <div>
              <BookingCta
                forMembership
                trackingLabel="bloodtest_membership_card_cta"
                trackingLocation="go/bloodtest"
                className="block"
              >
                <Button
                  variant="primary"
                  className="group w-full"
                  iconRight={
                    <ArrowRightIcon className="transition-transform duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:translate-x-1" />
                  }
                >
                  {t("bloodMembershipCta")}
                </Button>
              </BookingCta>
            </div>

            {/* Row 4: checklist */}
            <div className="flex flex-col">
              {BENEFIT_ROW_KEYS.map((row) => (
                <BenefitRow key={row.key} label={t(row.key)} included />
              ))}
            </div>
          </div>
        )}

        {/* ── Right: One-off blood test (Advanced) ── */}
        <div
          className={cn(
            "row-span-4 grid grid-rows-subgrid gap-y-6 rounded-[20px] bg-white p-5 md:p-8",
            CARD_SHADOW
          )}
        >
          {/* Row 1: header */}
          <div className="flex flex-col">
            <div className="flex items-center gap-3">
              <span className="relative size-10 flex-shrink-0 overflow-hidden rounded-full">
                <Image
                  src={blobMediaUrl(
                    "/images/homepage/service-blood-cells.jpeg"
                  )}
                  alt=""
                  fill
                  sizes="40px"
                  className="object-cover"
                />
              </span>
              <h3 className="type-headline-md text-neutral-900">
                {t("bloodMembershipOneOffTitle")}
              </h3>
            </div>
            <p className="type-body-md mt-2 text-neutral-500">
              {t("bloodMembershipOneOffTagline")}
            </p>
            <p className="type-body-lg mt-2 text-neutral-900">
              {t("bloodMembershipOneOffDescription")}
            </p>
          </div>

          {/* Row 2: price */}
          <div className="flex items-baseline gap-1">
            <span className="type-body-md text-neutral-500">CHF</span>
            <span className="type-display-sm text-neutral-900">299</span>
          </div>

          {/* Row 3: CTA */}
          <div>
            <BookingCta
              productId={PRODUCT_IDS.bloodTest}
              trackingLabel="bloodtest_oneoff_card_cta"
              trackingLocation="go/bloodtest"
              className="block"
            >
              <Button
                variant="primary-alt"
                className="group w-full"
                iconRight={
                  <ArrowRightIcon className="transition-transform duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:translate-x-1" />
                }
              >
                {/* Reused: EN identical to the booking-comparison CTA. */}
                {t("bloodtestCompareBaselineCta")}
              </Button>
            </BookingCta>
          </div>

          {/* Row 4: checklist */}
          <div className="flex flex-col">
            {BENEFIT_ROW_KEYS.map((row) => (
              <BenefitRow
                key={row.key}
                label={t(row.key)}
                included={row.inOneOff}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="mt-8 flex justify-center">
        <Link href={lp("/membership")}>
          <Button
            variant="outline"
            className="group"
            iconRight={
              <ArrowRightIcon className="transition-transform duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:translate-x-1" />
            }
          >
            {t("bloodMembershipLearnMore")}
          </Button>
        </Link>
      </div>
    </div>
  )
}
